import React, { useState } from 'react';

import * as valid from 'utils/validators';
import PaperContainer from 'ui/PaperContainer';
import Fields from 'ui/form/FieldsForm';
import ButtonsForm from 'ui/form/ButtonsForm';
import StatusActionForm from 'ui/form/StatusActionForm';

import { extendByDefaultSchedule } from '../../schedule';
import { HelpLink, AuthenticationInfo } from '../../types/common/Forms';
import { PasswordField } from '../../types/common/Components';

const FORM_VALIDATORS = valid.rules([
    valid.notEmpty('username'),
    valid.notEmpty('password'),
]);

const validateForm = data => valid.validate(FORM_VALIDATORS, data);

const INIT_VALUES = {
    username: '',
    password: '',
    integration_status: false,
};

export default function AutoTaskCredentialsView({
    created = false,
    service = {},
    context,
    onSave,
    onVerify,
    onCancel,
    onRemove,
}) {
    const [disabled, setDisabled] = useState(created);

    const [status, setStatus] = useState({
        loading: false,
        success: !created,
        failed: false,
    });
    const { extra = {} } = service;
    const { config } = context;
    const [values, setValues] = useState({ ...INIT_VALUES, ...extra });
    const [errors, setErrors] = useState({});

    const changeFactory = name => value => {
        if (name !== 'integration_status') {
            setStatus({});
        }
        setValues({
            ...values,
            [name]: typeof value === 'string' ? value.trim() : value,
        });
    };

    const fieldFactory = (name, label, secured = false) => {
        const Component = secured ? PasswordField : Fields.Text;
        return (
            <Fields.Row>
                <Component
                    label={label}
                    value={values[name]}
                    error={errors[name]}
                    onChange={changeFactory(name)}
                />
            </Fields.Row>
        );
    };

    const onVerifyValidate = () => {
        validateForm(values).then(errors => {
            setErrors(errors || {});
            if (!errors) {
                setStatus({ loading: true });
                onVerify({ ...values, integration_status: true }).then(
                    status => {
                        if (status) {
                            setStatus({ success: true });
                            setValues({ ...values, integration_status: true });
                            setDisabled(false);
                        } else {
                            setStatus({ failed: true });
                        }
                    }
                );
            }
        });
    };
    const onNext = () =>
        onSave(created ? extendByDefaultSchedule(values) : values);
    return (
        <PaperContainer title={`${config.label} Integration`}>
            <AuthenticationInfo config={config} />
            {fieldFactory('username', 'Username*')}
            {fieldFactory('password', 'Password*', true)}
            <StatusActionForm
                status={status}
                disabled={status.loading}
                onVerify={onVerifyValidate}
            />
            <IntegrationStatus
                value={values.integration_status}
                disabled={disabled || !status.success}
                onChange={changeFactory('integration_status')}
            />
            {onRemove && <HelpLink config={config} />}

            <ButtonsForm withLeft>
                {onRemove && (
                    <ButtonsForm.BackButton label="Delete" onClick={onRemove} />
                )}
                {!onRemove && <HelpLink config={config} />}
                <div>
                    {!created && (
                        <ButtonsForm.BackButton onClick={() => onCancel()} />
                    )}
                    <ButtonsForm.NextButton
                        label="Save"
                        disabled={disabled || !status.success}
                        onClick={onNext}
                    />
                </div>
            </ButtonsForm>
        </PaperContainer>
    );
}

const IntegrationStatus = ({ value, disabled, onChange }) => (
    <Fields.Row>
        <Fields.Switch
            value={value}
            onChange={onChange}
            legend="Integration status"
            label={value ? 'Enabled' : 'Disabled'}
            disabled={disabled}
        />
    </Fields.Row>
);
